import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import PhotoInput from "components/inputs/photos/PhotoInput";
import type Image from "models/Image";
import imageCompression from "browser-image-compression";
import { enqueueSnackbar } from "notistack";
import ToastMessages from "constants/ToastMessages";

type PhotosInputProps = {
  initialValue: Array<string | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (...event: Array<any>) => void;
  maximumPhotos?: number;
  readOnly?: boolean;
};
function PhotosInput({
  initialValue,
  onChange,
  maximumPhotos = 3,
  readOnly = false,
}: Readonly<PhotosInputProps>): ReactNode {
  const [images, setImages] = useState<Array<Image>>(
    (readOnly ? initialValue : []).map((base64String) => ({
      file: new File([], ""),
      base64String,
    }))
  );
  const inputFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const base64Array: Array<string> = [];
    images.forEach((image) => {
      if (image.base64String != null && image.base64String !== "") {
        base64Array.push(image.base64String);
      }
    });
    onChange(base64Array);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  async function handleImageUpload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    if (readOnly) return;
    type FileEventTarget = EventTarget & { files: FileList };
    /* Selected files data can be collected here. */
    if ((event.target as HTMLInputElement)?.files?.length != null) {
      const eTarget = event.target as FileEventTarget;
      const imageFile = eTarget.files[0];

      if (images.length < maximumPhotos) {
        const newArray = [...images];
        const image: Image = {
          file: new File([], ""),
          isLoading: true,
        };
        const index = newArray.push(image) - 1;
        setImages(newArray);

        const options = {
          maxSizeMB: 0.1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        try {
          const base64CompressedImage = await imageCompression.getDataUrlFromFile(
            await imageCompression(imageFile, options)
          );
          const newImages = [...images];
          const newImage: Image = {
            file: imageFile,
            base64String: base64CompressedImage,
            isLoading: false,
          };
          newImages[index] = newImage;
          setImages(newImages);
        } catch (error) {
          console.error("Image compression error :");
          console.error(error);
          setImages(newArray.filter((_image) => _image?.isLoading !== true));
          enqueueSnackbar(ToastMessages.ERROR_IMAGE_UPLOAD, {
            variant: "error",
          });
        }
      } else {
        console.warn("PhotosInput : new photo index > maximumPhotos");
      }
    }
    // eslint-disable-next-line no-param-reassign -- On nettoie la sélection actuelle de l'input pour la prochaine sélection
    event.target.value = "";
  }

  function selectImage(): void {
    /* Clique artificiellement sur l'input type='file' */
    inputFileRef?.current?.click();
  }

  function deleteImage(index: number): void {
    if (images[index] != null) {
      const newImages = [...images];
      newImages.splice(index, 1);
      setImages(newImages);
    }
  }

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <>
      {/* cet input caché est celui qui est artificiellement clické pour
        permettre au user de sélectionner sa photo */}
      {!readOnly && (
        <input
          type="file"
          /** Ajouter une string bullshit arbitraire, ici `android/force-camera-workaround`, permet de laisser le choix à l'utilisateur
           * entre ouvrir la caméra pour prendre une photo directement, ou ouvrir le système de fichiers pour sélectionner des photos déjà prises.
           * https://stackoverflow.com/questions/77876374/html-input-type-file-not-working-to-pull-up-camera-for-pixel-android-14-comb/79163998#79163998
           */
          accept="image/*, android/force-camera-workaround"
          ref={inputFileRef}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onChange={handleImageUpload}
          style={{ display: "none" }}
        />
      )}
      <Grid container spacing={smUp ? 3 : 2} size={12}>
        {readOnly && images?.length <= 0 ? (
          <Grid size={12}>
            <p style={{ margin: 0 }}>Aucune photo n'a été ajoutée à cette déclaration.</p>
          </Grid>
        ) : (
          /* petit trick pour avoir un tableau [0,1,2...maximumPhotos - 1] en Typescript */
          Array.from(Array(maximumPhotos).keys()).map((index: number) => (
            <Grid key={index} size={{ xs: 6, sm: 4 }}>
              <PhotoInput
                readOnly={readOnly}
                isLoading={images[index]?.isLoading}
                base64String={images[index]?.base64String}
                deleteImage={(): void => {
                  deleteImage(index);
                }}
                selectImage={selectImage}
              />
            </Grid>
          ))
        )}
      </Grid>
    </>
  );
}

export default PhotosInput;
